/**
 * One key factory per query domain. Each call builds exactly [domain, ...parts], so
 * queryKeys.modules(courseCode) === ["modules", courseCode] and queryKeys.modules()
 * is the prefix for invalidating every "modules" query. Add new domains here instead
 * of writing key arrays inline, so a misspelt domain is a type error.
 */
const domain =
  <const Domain extends string>(name: Domain) =>
  <const Parts extends readonly unknown[]>(...parts: Parts) =>
    [name, ...parts] as const;

export const queryKeys = {
  adminAnalytics: domain("admin-analytics"),
  adminCoupons: domain("admin-coupons"),
  adminCourseAnalytics: domain("admin-course-analytics"),
  adminDashboard: domain("admin-dashboard"),
  adminDashboardStats: domain("admin-dashboard-stats"),
  adminTeacherDashboard: domain("admin-teacher-dashboard"),
  analytics: domain("analytics"),
  auditEnrollments: domain("audit-enrollments"),
  auditLogs: domain("audit-logs"),
  auditOrders: domain("audit-orders"),
  auditTransactions: domain("audit-transactions"),
  banners: domain("banners"),
  blockedIps: domain("blocked-ips"),
  broadcastCourses: domain("broadcast-courses"),
  cmsBlogs: domain("cms-blogs"),
  cmsTestimonials: domain("cms-testimonials"),
  collegeOptions: domain("college-options"),
  colleges: domain("colleges"),
  coupon: domain("coupon"),
  couponAnalytics: domain("coupon-analytics"),
  couponAnalyticsSecurity: domain("coupon-analytics-security"),
  couponConfig: domain("coupon-config"),
  couponDetails: domain("coupon-details"),
  couponSecurity: domain("coupon-security"),
  couponStats: domain("coupon-stats"),
  couponThresholds: domain("coupon-thresholds"),
  course: domain("course"),
  courseByCode: domain("course-by-code"),
  courseDetails: domain("course-details"),
  courseModules: domain("course-modules"),
  coursePlans: domain("course-plans"),
  coursePlansTeacher: domain("course-plans-teacher"),
  courses: domain("courses"),
  emergencyDashboard: domain("emergency-dashboard"),
  enrolledStudents: domain("enrolled-students"),
  mcqSet: domain("mcq-set"),
  mcqSets: domain("mcq-sets"),
  media: domain("media"),
  moduleContents: domain("module-contents"),
  modules: domain("modules"),
  orderCouponStatus: domain("order-coupon-status"),
  orderInspection: domain("order-inspection"),
  orderRefunds: domain("order-refunds"),
  platformOverview: domain("platform-overview"),
  privacyPolicySync: domain("privacy-policy-sync"),
  privacyPolicyVersions: domain("privacy-policy-versions"),
  redemptionHistory: domain("redemption-history"),
  securityDashboard: domain("security-dashboard"),
  securityDashboardEmergency: domain("security-dashboard-emergency"),
  securityHealthWidget: domain("security-health-widget"),
  securityReport: domain("security-report"),
  sessions: domain("sessions"),
  studentCountByCourse: domain("student-count-by-course"),
  studentEnrollmentsAudit: domain("student-enrollments-audit"),
  teacherAuditEnrollments: domain("teacher-audit-enrollments"),
  teacherAuditEnrollmentsSummary: domain("teacher-audit-enrollments-summary"),
  teacherCoupons: domain("teacher-coupons"),
  teacherCourses: domain("teacher-courses"),
  teacherCoursesForCoupon: domain("teacher-courses-for-coupon"),
  teacherDashboard: domain("teacher-dashboard"),
  teacherDashboardCourses: domain("teacher-dashboard-courses"),
  teacherDashboardRealtime: domain("teacher-dashboard-realtime"),
  teacherInvitations: domain("teacher-invitations"),
  teacherOrders: domain("teacher-orders"),
  teacherProfile: domain("teacher-profile"),
  teacherProfileImage: domain("teacher-profile-image"),
  teacherStudentDetail: domain("teacher-student-detail"),
  teachers: domain("teachers"),
  topPerforming: domain("top-performing"),
  users: domain("users"),
} as const;
